import React, { InputHTMLAttributes, forwardRef, ForwardedRef } from 'react';
import { CheckIcon } from './Icons';

interface TeslaCheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: React.ReactNode;
  error?: string;
  className?: string;
}

/**
 * Tesla-inspired checkbox component
 */
const TeslaCheckbox = forwardRef((
  {
    label,
    error,
    className = '',
    id,
    checked,
    disabled,
    ...props
  }: TeslaCheckboxProps,
  ref: ForwardedRef<HTMLInputElement>
) => {
  // Generate a unique ID if none is provided
  const checkboxId = id || `checkbox-${Math.random().toString(36).substr(2, 9)}`;
  
  // Determine checkbox container classes based on props
  const containerClasses = [
    'checkbox-container',
    error ? 'checkbox-container--error' : '',
    disabled ? 'checkbox-container--disabled' : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClasses}>
      <label htmlFor={checkboxId} className="checkbox-label">
        <div className={`checkbox-control ${checked ? 'checkbox-control--checked' : ''}`}>
          <input
            ref={ref}
            id={checkboxId}
            type="checkbox"
            className="checkbox-input"
            checked={checked}
            disabled={disabled}
            {...props}
          />
          {checked && (
            <div className="checkbox-icon">
              <CheckIcon size={16} />
            </div>
          )}
        </div>
        <span className="checkbox-text">{label}</span>
      </label>
      
      {error && <div className="checkbox-error">{error}</div>}
    </div>
  );
});

TeslaCheckbox.displayName = 'TeslaCheckbox';

export default TeslaCheckbox;